import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core'

/** Cómo se reparte un pago entre varios arrendamientos. Espeja `DistributionMode` del backend. */
export type DistributionMode = 'CASCADE' | 'EQUAL_PARTS'

/**
 * Un arrendamiento al que se le puede mandar dinero.
 *
 * `capacity` llega **desde fuera** a propósito. Con la mora encendida, cuánto debe una letra depende de
 * una función que capitaliza a diario, necesita los movimientos de dos colecciones que ningún endpoint
 * expone y corta a la fecha del pago: el panel no puede calcularla, y si lo intentara se equivocaría un
 * 40 % según las propias pruebas del motor. Hoy la calcula quien monta el componente sumando el pendiente
 * de las letras elegibles; el día que exista el endpoint agregado, solo cambia de dónde sale.
 */
export interface AllocatableLease {
	leaseId: string
	folio: string
	/** Lo que el arrendamiento puede absorber: renta pendiente más mora exigible. */
	capacity: number
	/** Desglose para que el operador vea qué parte de la capacidad es mora, que es la que crece sola. */
	rentPending: number
	moraPending: number
	openLetters: number
	/** Días de atraso de la letra vencida más antigua. Null si no hay ninguna vencida. */
	oldestOverdueDays: number | null
	anticipatedCount: number
}

/** Lo que recibe un arrendamiento con el reparto vigente. */
export interface LeaseAllocation {
	leaseId: string
	folio: string
	/** Cuánto le llega. En cascada es exacto; en partes iguales es el presupuesto acotado por su capacidad. */
	amount: number
	capacity: number
	/** Queda saldado con lo que le llega. */
	settles: boolean
	/** Está marcado pero no le alcanza nada: el dinero se acabó antes. */
	starved: boolean
}

/** Lo que el componente entrega al que lo monta: listo para armar el cuerpo de la petición. */
export interface DistributionResult {
	distribution: DistributionMode
	allocations: LeaseAllocation[]
	/** Lo que sobra del monto a aplicar después de repartir. Se queda disponible en el pago. */
	leftover: number
	/** Hay dos o más marcados y el operador todavía no confirma el reparto. */
	needsConfirmation: boolean
}

/**
 * Decide a qué arrendamientos va un pago cuando el cliente tiene varios activos, y en qué orden.
 *
 * Existe porque el reparto del motor no era el que el negocio espera: repartía en partes iguales, así que
 * con tres arrendamientos marcados un pago que alcanzaba para cerrar una letra se partía en tres tercios y
 * dejaba tres a medias. El owner pidió las dos formas, porque el criterio cambia según el cliente.
 *
 * Las cifras que pinta están cortadas a la FECHA DEL PAGO, no a hoy: es lo que el motor va a cobrar. Por eso
 * la pantalla ancla la lista a esa fecha en vez de llamarlas «estimadas» — un adjetivo hace desconfiar de un
 * número que es exacto para su corte, y empuja a recalcular a mano.
 */
@Component({
	selector: 'app-lease-distribution-selector',
	templateUrl: './lease-distribution-selector.component.html',
	styleUrls: ['./lease-distribution-selector.component.scss'],
})
export class LeaseDistributionSelectorComponent implements OnChanges {
	/** Los arrendamientos activos del cliente. El orden inicial lo decide quien monta (lo natural: más atraso primero). */
	@Input() leases: AllocatableLease[] = []
	/** Cuánto del pago se va a aplicar. */
	@Input() amount = 0
	/** La fecha a la que están cortadas las cifras: la del depósito del cliente. */
	@Input() asOf: string | Date | null = null
	/** Si el arrendamiento desde el que se abrió debe venir marcado y primero. */
	@Input() preselectedLeaseId = ''
	@Input() disabled = false

	@Output() distributionChange = new EventEmitter<DistributionResult>()

	//* Para devolver el foco a la fila después de moverla: Angular resuelve el reordenamiento como un
	//* detach + insert del nodo, y un elemento que sale del documento se lleva el foco a `<body>`.
	@ViewChildren('fila') private filas!: QueryList<ElementRef<HTMLElement>>

	/** El orden vigente. Es el que viaja al backend y el que manda en cascada. */
	ordered: AllocatableLease[] = []
	checked = new Set<string>()
	mode: DistributionMode = 'CASCADE'
	confirmed = false
	/** Lo que se anuncia cuando el reparto cambia, para quien no ve la lista. */
	liveMessage = ''
	/** El último movimiento de fila, para anunciarlo junto al reparto. Se consume al anunciar. */
	private movimiento = ''

	private dragIndex: number | null = null

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['leases']) this.reset()
		//* Cambiar el monto rehace el reparto pero NO el orden ni las casillas: el operador no pierde su trabajo
		//* por corregir una cifra.
		this.emit()
	}

	/**
	 * Un arrendamiento sin saldo que cobrar no se puede elegir.
	 *
	 * No es cosmética: el motor rechaza la petición ENTERA con 400 si a uno de los arrendamientos del arreglo
	 * no le quedan letras elegibles. Con seis marcados, uno sin saldo tumba los seis. Es mejor no ofrecerlo
	 * que dejar que el operador lo marque y reciba un error por todo el conjunto.
	 */
	selectable(l: AllocatableLease): boolean {
		return l.capacity > 0.01
	}

	/**
	 * Lo que se le anuncia a quien no ve la fila.
	 *
	 * La sin saldo llevaba la misma coletilla de «Alt más flecha para mover» que el resto, y ese gesto
	 * ahí no hace nada: `toggle` y `onRowKey` salen sin tocar nada. Se le decía al operador de teclado
	 * que hiciera algo imposible, y no se le decía lo único que importaba —que ese arrendamiento no se
	 * puede incluir—, porque eso solo estaba en el texto visual que la propia etiqueta tapa.
	 */
	rowLabel(lease: AllocatableLease, i: number): string {
		if (!this.selectable(lease)) return `${lease.folio}. Sin saldo que cobrar, no se puede incluir.`
		const estado = this.checked.has(lease.leaseId) ? 'incluido' : 'excluido'
		return `${lease.folio}. ${estado}. Posición ${i + 1} de ${this.ordered.length}. Alt más flecha arriba o abajo para mover.`
	}

	private reset(): void {
		this.ordered = [...(this.leases || [])]
		//* El arrendamiento desde el que se abrió el diálogo va primero y marcado: el operador venía mirándolo,
		//* y esa es su intención declarada.
		if (this.preselectedLeaseId) {
			const i = this.ordered.findIndex((l) => l.leaseId === this.preselectedLeaseId)
			if (i > 0) this.ordered.unshift(...this.ordered.splice(i, 1))
		}
		//* Por defecto viene marcado SOLO el primero, nunca todos: marcar todos convierte un descuido en dinero
		//* repartido donde no iba.
		const primero = this.ordered.find((l) => this.selectable(l))
		this.checked = new Set(primero ? [primero.leaseId] : [])
		this.confirmed = false
	}

	// ── Reparto ──────────────────────────────────────────────────────────────

	get selected(): AllocatableLease[] {
		return this.ordered.filter((l) => this.checked.has(l.leaseId))
	}

	/**
	 * Cuánto le llega a cada arrendamiento con el orden, las casillas y el modo vigentes.
	 *
	 * Se acota siempre por la capacidad: lo que el motor asigna como presupuesto no es lo que aterriza en las
	 * letras si el arrendamiento no puede absorberlo. Enseñar el presupuesto sería prometer un impacto que no
	 * ocurre.
	 */
	get allocations(): LeaseAllocation[] {
		return this.computeAllocations()
	}

	/**
	 * El reparto vigente indexado por ARRENDAMIENTO, que es como la plantilla tiene que leerlo.
	 *
	 * `allocations` va comprimido sobre los marcados, así que su posición no es la de la fila: con la
	 * primera fila desmarcada, `allocations[0]` es el reparto de la segunda. La plantilla lo indexaba
	 * por la posición de `ordered` y cada fila de abajo enseñaba el importe, el «queda saldado» y el
	 * atenuado de OTRO arrendamiento — y la fila desmarcada pintaba a la vez un importe y su «excluido».
	 * Ocurría nada más abrir el diálogo si el primer arrendamiento de la lista no tenía saldo, porque
	 * `reset()` marca el primero SELECCIONABLE, no el primero.
	 */
	get allocationByLease(): Map<string, LeaseAllocation> {
		return new Map(this.computeAllocations().map((a) => [a.leaseId, a]))
	}

	/** Lo que le llega a un arrendamiento concreto, o `undefined` si no está marcado. */
	allocationOf(lease: AllocatableLease): LeaseAllocation | undefined {
		return this.allocationByLease.get(lease.leaseId)
	}

	private computeAllocations(): LeaseAllocation[] {
		const marcados = this.selected
		if (!marcados.length || this.amount <= 0) return []

		const presupuesto = this.mode === 'EQUAL_PARTS' ? this.round2(this.amount / marcados.length) : 0
		let restante = this.amount

		return marcados.map((l) => {
			const tope = this.mode === 'CASCADE' ? restante : Math.min(presupuesto, restante)
			const asignado = this.round2(Math.max(0, Math.min(tope, l.capacity)))
			restante = this.round2(restante - asignado)
			return {
				leaseId: l.leaseId,
				folio: l.folio,
				amount: asignado,
				capacity: l.capacity,
				settles: asignado > 0 && asignado >= this.round2(l.capacity),
				starved: asignado <= 0,
			}
		})
	}

	get assigned(): number {
		return this.round2(this.allocations.reduce((s, a) => s + a.amount, 0))
	}

	get leftover(): number {
		return this.round2(Math.max(0, this.amount - this.assigned))
	}

	/**
	 * El arrendamiento donde se acaba el dinero. Vacío si alcanza para todos los marcados.
	 *
	 * Se identifica por folio y no por posición: `allocations` no comparte espacio de índices con
	 * `ordered`, así que comparar un índice contra el otro dibujaba la línea bajo la fila equivocada.
	 */
	get cutLeaseId(): string {
		const corte = this.computeAllocations().find((a) => a.amount < this.round2(a.capacity))
		return corte ? corte.leaseId : ''
	}

	/**
	 * Si debe dibujarse la línea de «hasta aquí alcanza» DESPUÉS de la fila `i`.
	 *
	 * Solo en cascada. Ahí el orden decide quién cobra y quién se queda sin nada, así que la línea
	 * marca un límite real. En partes iguales todos los marcados reciben su parte: no hay nadie «del
	 * otro lado del corte», y dibujarla decía que el dinero no llegaba a una fila que sí estaba
	 * cobrando. Se vio en el recorrido por pantalla, no en las pruebas.
	 */
	showCutAfter(i: number): boolean {
		if (this.mode !== 'CASCADE' || this.leftover > 0.005) return false
		if (i >= this.ordered.length - 1) return false
		return this.ordered[i]?.leaseId === this.cutLeaseId
	}

	/** Cuántos de los marcados reciben dinero de verdad. Puede ser menos que los marcados. */
	get receivingCount(): number {
		return this.allocations.filter((a) => a.amount > 0).length
	}

	/** Con dos o más destinos el operador confirma el reparto: es dinero que se parte. */
	get needsConfirmation(): boolean {
		return this.selected.length > 1
	}

	get ready(): boolean {
		return this.selected.length > 0 && this.assigned > 0 && (!this.needsConfirmation || this.confirmed)
	}

	// ── Interacción ──────────────────────────────────────────────────────────

	toggle(lease: AllocatableLease): void {
		if (this.disabled || !this.selectable(lease)) return
		if (this.checked.has(lease.leaseId)) this.checked.delete(lease.leaseId)
		else this.checked.add(lease.leaseId)
		this.invalidate()
	}

	setMode(mode: DistributionMode): void {
		if (this.disabled || this.mode === mode) return
		this.mode = mode
		this.invalidate()
	}

	onDragStart(i: number): void {
		if (!this.disabled) this.dragIndex = i
	}

	onDrop(target: number): void {
		if (this.disabled || this.dragIndex === null || this.dragIndex === target) return
		this.move(this.dragIndex, target)
		this.dragIndex = null
	}

	/**
	 * Mover con el teclado, con la misma gramática que el Backlog del Tablero: Alt + flechas.
	 *
	 * Arrastrar con el ratón no basta. El orden decide a quién le llega el dinero primero, así que dejarlo
	 * solo en un gesto de puntero deja fuera a quien navega con teclado de una decisión de dinero.
	 */
	onRowKey(ev: KeyboardEvent, i: number): void {
		if (this.disabled) return
		if (ev.key === ' ' || ev.key === 'Enter') {
			ev.preventDefault()
			this.toggle(this.ordered[i])
			return
		}
		//* Reordenar uno que no se puede elegir no cambia nada del reparto, pero sí despista.
		if (!this.selectable(this.ordered[i])) return
		if (!ev.altKey) return
		if (ev.key === 'ArrowUp' && i > 0) {
			ev.preventDefault()
			this.move(i, i - 1)
		} else if (ev.key === 'ArrowDown' && i < this.ordered.length - 1) {
			ev.preventDefault()
			this.move(i, i + 1)
		}
	}

	private move(from: number, to: number): void {
		const [item] = this.ordered.splice(from, 1)
		this.ordered.splice(to, 0, item)
		//* El movimiento se anuncia SIEMPRE, aunque no cambie el reparto: con un solo marcado, reordenar
		//* no mueve dinero, y sin este acuse quien usa el teclado no sabría si su gesto hizo algo.
		this.movimiento = `${item.folio} ahora es el ${to + 1} de ${this.ordered.length}.`
		this.invalidate()
		this.focusRow(to)
	}

	/**
	 * Devuelve el foco a la fila que se acaba de mover.
	 *
	 * `*ngFor` con `trackBy` resuelve el reordenamiento moviendo el nodo del DOM, y un nodo que sale del
	 * documento pierde el foco. Sin esto, el PRIMER Alt+flecha movía la fila y el SEGUNDO no llegaba a
	 * ningún manejador: quien ordena con el teclado se quedaba sin poder seguir, justo en el gesto que
	 * decide a quién le llega el dinero primero.
	 */
	private focusRow(i: number): void {
		//* Después de la detección de cambios: antes, el nodo todavía está en su sitio viejo.
		setTimeout(() => this.filas?.get(i)?.nativeElement?.focus(), 0)
	}

	/**
	 * Cualquier cambio del reparto tira la confirmación.
	 *
	 * Si no se tirara, el operador podría confirmar un reparto y aplicar otro: confirmó «A recibe $30,000»,
	 * movió una fila, y el cuerpo sale con «A recibe $2,000» y la casilla todavía marcada.
	 */
	private invalidate(): void {
		this.confirmed = false
		this.announce()
		this.emit()
	}

	/**
	 * Qué se anuncia cuando el reparto cambia.
	 *
	 * Antes solo hablaba al reordenar, y decía la POSICIÓN: «F-001 ahora es el 2 de 3». Callaba lo único
	 * que el movimiento provocó —que F-001 pasó de cobrar $30,000 a cobrar $2,000— y callaba del todo al
	 * marcar, al desmarcar y al cambiar de modo, que es cuando aparece de la nada una casilla obligatoria
	 * que bloquea la previsualización. Quien no ve la lista no se enteraba de ninguna de las dos cosas.
	 */
	private announce(): void {
		const reparto = this.allocations
		if (!reparto.length) {
			const movido = this.movimiento ? this.movimiento + ' ' : ''
			this.movimiento = ''
			this.liveMessage = `${movido}Ningún arrendamiento marcado: el pago queda sin aplicar.`
			return
		}
		const detalle = reparto.map((a) => `${a.folio} recibe ${this.pesos(a.amount)}${a.settles ? ', queda saldado' : a.starved ? ', no le llega nada' : ''}`).join('. ')
		const modo = this.mode === 'CASCADE' ? 'En cascada' : 'En partes iguales'
		const pendiente = this.needsConfirmation && !this.confirmed ? ' Falta confirmar el reparto para poder previsualizar.' : ''
		const movido = this.movimiento ? this.movimiento + ' ' : ''
		this.movimiento = ''
		this.liveMessage = `${movido}${modo}, ${reparto.length} de ${this.ordered.length} marcados. ${detalle}.${pendiente}`
	}

	private pesos(n: number): string {
		return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 })
	}

	onConfirmChange(): void {
		this.emit()
	}

	private emit(): void {
		this.distributionChange.emit({
			distribution: this.mode,
			allocations: this.allocations,
			leftover: this.leftover,
			needsConfirmation: this.needsConfirmation && !this.confirmed,
		})
	}

	// ── Presentación ─────────────────────────────────────────────────────────

	/** Qué fracción de la barra ocupa cada arrendamiento. */
	widthOf(a: LeaseAllocation): string {
		return this.amount > 0 ? `${(a.amount / this.amount) * 100}%` : '0%'
	}

	trackByLease(_: number, l: AllocatableLease): string {
		return l.leaseId
	}

	private round2(n: number): number {
		return Math.round((n + Number.EPSILON) * 100) / 100
	}
}
